import { useState } from 'react';
import { RefreshCw, Plus, Trash2, ChevronDown, ChevronUp, Code, AlertCircle, CheckCircle2 } from 'lucide-react';
import Button from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import Badge from '@/components/common/Badge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { usePlugins, useEnablePlugin, useDisablePlugin, useReloadPlugins } from '@/hooks/usePlugins';

function PluginCard({ plugin, onEnable, onDisable }) {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-lg">{plugin.name}</h3>
            <Badge variant="secondary">v{plugin.version}</Badge>
            {plugin.hasError && (
              <Badge variant="destructive">
                <AlertCircle className="h-3 w-3 mr-1" />
                Error
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-2">
            {plugin.description || 'No description available'}
          </p>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>Source: {plugin.source || 'unknown'}</span>
            {plugin.actionCount !== undefined && (
              <span>{plugin.actionCount} action(s)</span>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Enable/Disable Toggle */}
          <Button
            variant={plugin.enabled ? 'destructive' : 'default'}
            size="sm"
            onClick={() => plugin.enabled ? onDisable(plugin.name) : onEnable(plugin.name)}
          >
            {plugin.enabled ? 'Disable' : 'Enable'}
          </Button>
          
          {/* Expand/Collapse */}
          {plugin.actions?.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          )}
        </div>
      </div>
      
      {/* Expanded section */}
      {expanded && plugin.actions?.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center gap-2 mb-3">
            <Code className="h-4 w-4" />
            <span className="text-sm font-medium">Actions provided by this plugin:</span>
          </div>
          <div className="space-y-2">
            {plugin.actions.map((action, idx) => (
              <div key={idx} className="bg-muted/50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-sm font-medium">{action.name}</span>
                  {action.category && <Badge variant="outline">{action.category}</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">{action.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Error display */}
      {plugin.hasError && plugin.error && (
        <div className="mt-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-destructive">Plugin Error</p>
              <p className="text-xs text-destructive/80 mt-1">{plugin.error}</p>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function Plugins() {
  const { data: plugins, isLoading } = usePlugins();
  const enablePlugin = useEnablePlugin();
  const disablePlugin = useDisablePlugin();
  const reloadPlugins = useReloadPlugins();
  
  const enabledCount = plugins?.filter(p => p.enabled).length || 0;
  const totalCount = plugins?.length || 0;
  
  const handleEnable = (name) => {
    enablePlugin.mutate(name);
  };
  
  const handleDisable = (name) => {
    disablePlugin.mutate(name);
  };
  
  const handleReload = () => {
    reloadPlugins.mutate();
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Plugins</h1>
          <p className="text-muted-foreground mt-2">
            Extend Boswell with email analysis plugins
          </p>
        </div>
        <Button 
          onClick={handleReload} 
          disabled={reloadPlugins.isPending}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${reloadPlugins.isPending ? 'animate-spin' : ''}`} />
          Reload Plugins
        </Button>
      </div>
      
      {/* Stats */}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Total Plugins:</span>
          <Badge variant="secondary">{totalCount}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Active:</span>
          <Badge variant="success">{enabledCount}</Badge>
        </div>
      </div>
      
      {/* Info Box */}
      <div className="p-4 rounded-lg border bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-1">
              How Plugins Work
            </h3>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Plugins analyze incoming emails to extract additional data like product prices, 
              order information, and other structured data. Enable or disable plugins to 
              control what data is extracted. Some plugins provide custom actions that can be 
              used in rules and automation.
            </p>
          </div>
        </div>
      </div>
      
      {/* Plugins List */}
      {isLoading ? (
        <div className="py-12">
          <LoadingSpinner />
        </div>
      ) : plugins && plugins.length > 0 ? (
        <div className="space-y-4">
          {plugins.map((plugin) => (
            <PluginCard
              key={plugin.name}
              plugin={plugin}
              onEnable={handleEnable}
              onDisable={handleDisable}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 border rounded-lg">
          <p className="text-muted-foreground mb-4">
            No plugins installed yet.
          </p>
          <p className="text-sm text-muted-foreground">
            Install plugins by adding them to your configuration or packages directory.
          </p>
        </div>
      )}
    </div>
  );
}